import React from "react";
import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { RelatedLink } from "@/components/RelatedPages";
import { buildMedicalClinicLocal, Crumb } from "@/components/schemaConstants";
import CrisisLifelineBanner from "@/components/CrisisLifelineBanner";

/**
 * /depression-treatment-coral-springs — the hub page.
 *
 * In-network claim limited to Medicare and the VA; "we verify your benefits
 * free" for all other carriers. No guarantees; eligibility decided at
 * consultation. SAINT® is for adults 22+. Ends with the 988 crisis line.
 */
const faqs: FAQItem[] = [
  {
    question: "Do I need a referral?",
    answer:
      "No. A referral is not required to book a free consultation. You can call us directly. If you have a current psychiatrist or primary care doctor, we are happy to coordinate with them with your permission.",
  },
  {
    question: "How fast can I be seen?",
    answer:
      "We offer early and late appointment times and work to get new patients in quickly. When you call, we will give you the earliest available consultation slot that fits your schedule.",
  },
  {
    question: "Do you treat depression without medication?",
    answer:
      "Yes. TMS is medication-free, and therapy is available with or without medication. For some patients, a non-drug option is the right fit; for others, medication management is part of the plan. Your physician decides what is appropriate after an evaluation.",
  },
  {
    question: "Do you take Medicare?",
    answer:
      "Yes. Neuro Wellness is an in-network Medicare provider. We also verify your benefits for free for any other carrier you have. Coverage varies by plan; we confirm it before you start.",
  },
  {
    question: "What ages do you treat?",
    answer:
      "We treat adults, including young adults 18–25 and seniors 65+. SAINT® accelerated TMS is available for adults 22 and older. We have dedicated pages for young adults and seniors.",
  },
  {
    question: "What happens at the free consultation?",
    answer:
      "You meet with our team to talk through your symptoms, history, and goals. We do a benefits check and explain your options — therapy, medication management, TMS, SPRAVATO®, ketamine, or a combination — with your costs explained up front. There is no obligation.",
  },
];

const relatedLinks: RelatedLink[] = [
  {
    title: "TMS in Coral Springs",
    href: "/tms-coral-springs-fl",
    description: "Medication-free TMS therapy at our Coral Springs clinic.",
  },
  {
    title: "SAINT® TMS in Coral Springs",
    href: "/saint-therapy-coral-springs-fl",
    description: "Accelerated, fMRI-guided 5-day TMS for adults 22+.",
  },
  {
    title: "Depression Treatment Options",
    href: "/depression-treatment-options",
    description: "Compare the depression treatments we offer.",
  },
  {
    title: "Depression Treatment Cost",
    href: "/depression-treatment-cost",
    description: "What treatment costs and how to pay.",
  },
];

const breadcrumbs: Crumb[] = [
  { name: "Home", href: "/" },
  { name: "Depression Treatment", href: "/depression-treatment-coral-springs" },
];

const DepressionTreatmentCoralSpringsHub: React.FC = () => {
  return (
    <>
      <DepressionContentPage
        seo={{
          title:
            "Depression Treatment in Coral Springs, FL | TMS, SPRAVATO®, Therapy & Medication Management",
          description:
            "Outpatient depression treatment in Coral Springs for young adults, adults and seniors: TMS, SAINT®, SPRAVATO®, therapy and medication management. Medicare & VA in-network.",
          canonical: "/depression-treatment-coral-springs",
          keywords:
            "depression treatment Coral Springs, TMS Coral Springs, SAINT Coral Springs, SPRAVATO Coral Springs, depression clinic Coral Springs FL, depression help Broward County",
          breadcrumbs,
          extraSchema: [buildMedicalClinicLocal()],
        }}
        badge="Coral Springs, FL · Outpatient Clinic"
        heroTitle="Depression Treatment in Coral Springs, Florida"
        heroSubtitle="An outpatient depression clinic at 9734 W Sample Rd serving Broward and Palm Beach — from first-time help to depression that hasn't responded to medication."
        heroCtaPrimary="Book a Free Consultation"
        heroCtaPrimaryHref="/#consultation"
        reviewedBy="Daniel Pietras, MD"
        lastUpdated="2026-11-04"
        sections={[
          {
            heading: "Outpatient depression care in Coral Springs",
            variant: "text",
            body: (
              <>
                <p>
                  Neuro Wellness TMS Centers of America is an outpatient
                  depression clinic at 9734 West Sample Road, 2nd Floor, Suite
                  B, in Coral Springs, Florida. We serve patients from across
                  Broward and Palm Beach counties — whether this is your first
                  time reaching out for help or you are looking for options
                  after antidepressants haven&rsquo;t given you enough relief.
                </p>
                <p>
                  We offer a full range of outpatient depression treatments
                  under one roof: standard TMS, SAINT® accelerated TMS,
                  SPRAVATO®, ketamine, medication management, and individual and
                  group therapy. You don&rsquo;t need to know which treatment
                  you need before you call — a free consultation helps you
                  understand what may be appropriate for your situation.
                </p>
              </>
            ),
          },
          {
            heading: "Care for every stage of life",
            variant: "links",
            links: [
              {
                title: "Young Adults (18–25)",
                href: "/young-adult-depression-treatment",
                description:
                  "Depression treatment built around school, work, and life in your 20s.",
              },
              {
                title: "Adults",
                href: "/adult-depression-treatment",
                description:
                  "Outpatient depression care for working-age adults.",
              },
              {
                title: "Seniors (65+)",
                href: "/senior-depression-treatment",
                description:
                  "Depression care for older adults — in-network with Medicare.",
              },
            ],
          },
          {
            heading: "Our depression treatments",
            variant: "cards",
            cards: [
              {
                title: "TMS Therapy",
                body: "Medication-free magnetic stimulation for treatment-resistant depression. About 20 minutes a session, no downtime.",
              },
              {
                title: "SAINT® Accelerated TMS",
                body: "fMRI-guided, 5-day accelerated TMS protocol for adults 22 and older with major depression.",
              },
              {
                title: "SPRAVATO® (Esketamine)",
                body: "FDA-approved nasal spray for treatment-resistant depression, given in-office with monitoring.",
              },
              {
                title: "Ketamine",
                body: "IV ketamine infusions and ketamine-assisted psychotherapy for depression.",
              },
              {
                title: "Medication Management",
                body: "A careful review of every current prescription for fit, interactions, and side effects.",
              },
              {
                title: "Individual & Group Therapy",
                body: "Talk therapy tailored to the concerns you bring — relationships, loss, work, and life transitions.",
              },
            ],
          },
          {
            heading: "If treatment hasn't worked yet",
            variant: "text",
            body: (
              <>
                <p>
                  If you have tried antidepressants or therapy without enough
                  relief, you are not out of options. Treatment-resistant
                  depression is exactly what TMS, SAINT®, and SPRAVATO® are
                  designed to address. Learn more at{" "}
                  <a
                    href="/tms-for-treatment-resistant-depression"
                    className="text-accent font-semibold underline underline-offset-4"
                  >
                    TMS for treatment-resistant depression
                  </a>
                  .
                </p>
              </>
            ),
          },
          {
            heading: "Insurance",
            variant: "text",
            body: (
              <>
                <p>
                  Neuro Wellness is in-network with Medicare and the VA. For
                  every other carrier, we verify your benefits for free before
                  your first visit — coverage varies by plan, and we confirm
                  yours so there are no surprises. See{" "}
                  <a
                    href="/insurance-for-tms-depression-treatment"
                    className="text-accent font-semibold underline underline-offset-4"
                  >
                    insurance for TMS depression treatment
                  </a>{" "}
                  for how it works.
                </p>
              </>
            ),
          },
          {
            heading: "Nearby areas we serve",
            variant: "text",
            body: (
              <>
                <p>
                  From our Coral Springs clinic we regularly see patients from
                  nearby communities across northern Broward and southern Palm
                  Beach, including:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>
                    <a
                      href="/tms-parkland-fl"
                      className="text-accent font-semibold underline underline-offset-4"
                    >
                      Parkland
                    </a>
                  </li>
                  <li>
                    <a
                      href="/tms-coconut-creek"
                      className="text-accent font-semibold underline underline-offset-4"
                    >
                      Coconut Creek
                    </a>
                  </li>
                  <li>Margate</li>
                  <li>Tamarac</li>
                  <li>
                    <a
                      href="/tms-fort-lauderdale"
                      className="text-accent font-semibold underline underline-offset-4"
                    >
                      Fort Lauderdale
                    </a>
                  </li>
                  <li>
                    <a
                      href="/tms-boca-raton"
                      className="text-accent font-semibold underline underline-offset-4"
                    >
                      Boca Raton
                    </a>
                  </li>
                </ul>
              </>
            ),
          },
        ]}
        faq={faqs}
        faqHeading="Frequently Asked Questions"
        relatedLinks={relatedLinks}
        relatedHeading="Explore Related Resources"
        finalCtaHeading="Depression treatment, close to home"
        finalCtaText="Call our Coral Springs team at 954-903-1323 or book a free consultation. We verify your benefits at no cost, whatever carrier you have. Eligibility is decided at the consultation; individual results vary."
        finalCtaPrimaryLabel="Book a Free Consultation"
        finalCtaPrimaryHref="/#consultation"
        finalCtaSecondaryLabel="Verify My Insurance — Free"
        finalCtaSecondaryHref="tel:9549031323"
      />
      <div className="container-px mx-auto max-w-3xl px-4 pb-12">
        <CrisisLifelineBanner />
      </div>
    </>
  );
};

export default DepressionTreatmentCoralSpringsHub;
